import { cloneDeep } from 'lodash';
import uniqWith from 'lodash/uniqWith';
import zipObject from 'lodash/zipObject';
import moment from 'moment';

export const DATE_FORMATS = [
  'YYYY-MM',
  'YYYY-MM-DD',
  'YYYY/MM/DD',
  'YYYY.MM.DD',
  'DD-MM-YYYY',
  'MM/DD/YYYY',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DD HH:mm:ss',
  '[Week of ]YYYY-MM-DD',
  'MMM DD',
  'YYYY_MM'
];

export const DATE_FORMAT_MAPPING = {
  'YYYY-MM': 'MMM',
  'YYYY-MM-DD': 'MM/DD',
  'YYYY/MM/DD': 'MM/DD',
  'YYYY.MM.DD': 'YYYY.MM.DD',
  'DD-MM-YYYY': 'DD-MM-YYYY',
  'MM/DD/YYYY': 'MM/DD/YYYY',
  'YYYY-MM-DD HH:mm': 'YYYY-MM-DD HH:mm',
  'YYYY-MM-DD HH:mm:ss': 'YYYY-MM-DD HH:mm:ss',
  '[Week of ]YYYY-MM-DD': '[Week of ]MM/DD',
  'MMM DD': 'MMM DD',
  'YYYY_MM': 'YYYY-MM'
};

export function convertCstDataIntoTableCompatible(data, allowedColumns = null) {
  const { columns, metric_data, metric_count = [], rows = [] } = data;
  // if (rows?.length) {
  //   rows.push("#")
  //   metric_data.unshift(rows)
  // }

  if (!columns || !metric_data) {
    return { table_columns: [], table_rows: [] };
  }

  const processedColumns = columns.map(col => {
    let field = col;
    const isDate = moment(col, 'YYYY-MM', true).isValid();
    let label = col;
    if (isDate) {
      const monthName = moment(col, 'YYYY-MM').format('MMM');
      label = `${monthName}`;
      field = col.replace('-', '_');
    }
    return { original: col, field, label };
  });

  const filteredColumns = allowedColumns
    ? processedColumns.filter(col => allowedColumns.includes(col.field))
    : processedColumns;

  const table_columns = filteredColumns.map(col => ({
    field: col.field,
    label: checkOnlyDateFormatAndUpdateFormat(
      col.label,
      DATE_FORMAT_MAPPING[currentDateFormat(col.label, DATE_FORMATS)]
    )
  }));

  const table_rows = metric_data.map(row => {
    const obj = {};
    filteredColumns.forEach(col => {
      const colIndex = columns.indexOf(col.original);
      obj[col.field] = row[colIndex];
    });
    return obj;
  });
  const table_count = metric_count?.map(row => {
    const obj = {};
    filteredColumns.forEach(col => {
      const colIndex = columns.indexOf(col.original);
      obj[col.field] = row[colIndex];
    });
    return obj;
  }) || []

  return { table_columns, table_rows, table_count };
}

export function convertCstDataIntoTableCompatibleData(data, allowedColumns = null, addedRowColumnName = "#") {
  const columns = cloneDeep(data?.columns)
  const metric_data = cloneDeep(data?.metric_data)
  const metric_count = cloneDeep(data?.metric_count)
  const rows = cloneDeep(data?.rows)

  if (rows?.length) {
    columns?.unshift(addedRowColumnName)
    metric_data?.forEach((d, index) => {
      d?.unshift(rows?.[index])
    })
    metric_count?.forEach((d, index) => {
      d?.unshift(rows?.[index])
    })
  }

  if (!columns || !metric_data) {
    return { table_columns: [], table_rows: [] };
  }

  const processedColumns = columns.map(col => {
    let field = col;
    const isDate = moment(col, 'YYYY-MM', true).isValid();

    let label = col;
    if (isDate) {
      const monthName = moment(col, 'YYYY-MM').format('MMM');
      label = `${monthName}`;
      field = col.replace('-', '_');
    }
    return { original: col, field, label };
  });

  const filteredColumns = allowedColumns
    ? processedColumns.filter(col => allowedColumns.includes(col.field))
    : processedColumns;

  const table_columns = filteredColumns.map(col => ({
    field: col.field,
    label: checkOnlyDateFormatAndUpdateFormat(
      col.label,
      DATE_FORMAT_MAPPING[currentDateFormat(col.label, DATE_FORMATS)]
    )
  }));

  const table_rows = metric_data.map((row, index) => {
    const obj = {};
    filteredColumns.forEach(col => {
      const colIndex = columns.indexOf(col.original);
      obj[col.field] = row[colIndex];
    });
    return { ...obj, index: index };
  });

  const table_count = metric_count?.map(row => {
    const obj = {};
    filteredColumns.forEach(col => {
      const colIndex = columns.indexOf(col.original);
      obj[col.field] = row[colIndex];
    });
    return obj;
  }) || []

  return { table_columns, table_rows, table_count };
}

export function convertCstDataIntoBarChartCompatible(
  cstData,
  barChartDefaultOption = {},
  valueKey = 'Total',
  xAxisLabelKey = 'property'
) {
  let ddd = makeArrayOfObjectOutOfMetricsData(cstData) || [];
  const options = { ...barChartDefaultOption };
  if (options?.xAxis && options?.series) {
    options.xAxis.categories = ddd?.map(e => e?.[xAxisLabelKey]) || [];
    options.series[0].data = ddd?.map(e => e?.[valueKey]) || [];
  }
  return options;
}

export function seprateDateAndNonDateColumns(columns) {
  return columns.reduce(
    (acc, val) => {
      if (isValidDate(val)) {
        acc.dateFields.push(val);
      } else {
        acc.nonDateFields.push(val);
      }
      return acc;
    },
    {
      dateFields: [],
      nonDateFields: []
    }
  );
}

export function makeArrayOfObjectOutOfMetricsData(input = {}) {
  return input?.metric_data?.map(row => Object.fromEntries(row.map((val, i) => [input.columns[i], val]))) || [];
}

export function getMonthWithValues(item) {
  return Object.entries(item)
    .filter(([key]) => isValidDate(key))
    .sort(([a], [b]) => a.localeCompare(b))
    .reduce(
      (acc, [key, value]) => {
        const monthLabel = moment(key.replace('_', '-')).format('MMM');
        acc.months.push(monthLabel);
        acc.data.push(value);
        return acc;
      },
      { months: [], data: [] }
    );
}

export function getOnlyMonthsData(columns, metricData) {
  const result = {};
  columns.forEach((col, colIndex) => {
    if (isValidDate(col)) {
      result[col] = metricData.map(row => row[colIndex]);
    }
  });

  return result;
}

export function buildMonthSeriesForMultiLineChart(columns, rows, metricData, colors = '#8be2e3') {
  const dateColumnIndexes = columns.map((col, index) => (isValidDate(col) ? index : null)).filter(i => i !== null);
  return rows.map((rowName, rowIndex) => {
    const rowValues = dateColumnIndexes.map(colIndex => metricData[rowIndex][colIndex]);
    const color = Array.isArray(colors) ? colors[rowIndex % colors.length] : colors;
    return {
      name: rowName,
      data: rowValues,
      color: color
    };
  });
}

export function makeJsonFromMetricData(data) {
  if (!data?.columns || !data?.metric_data) return [];
  const { columns, metric_data } = data;
  const rowObjects = metric_data.map(row => {
    return zipObject(columns, row);
  });
  return uniqWith(rowObjects, _.isEqual);
}

export function isValidDate(value, formats = DATE_FORMATS) {
  if (typeof value !== 'string') return false;
  return moment(value, formats, true).isValid();
}

export function checkOnlyDateFormatAndUpdateFormat(dateString, updatedFormat, formats = DATE_FORMATS) {
  if (isValidDate(dateString, formats)) {
    let dd = moment(dateString, formats, true);
    return moment(dd).format(updatedFormat);
  }
  return dateString;
}

export function currentDateFormat(dateString, dateFormats = DATE_FORMATS) {
  for (const format of dateFormats) {
    if (moment(dateString, format, true).isValid()) {
      return format;
    }
  }
  return null;
}

export const filterValuesFromArray = (array = [], notAllowedValues = []) => {
  return array?.filter(item => !notAllowedValues.includes(item)) || [];
};

export function transformAlertMetrics(input = {}, timeperiodLabel) {
  if (!input?.metric_data || !input?.columns || !input?.rows) return {};

  const colMap = input.columns.map(c => c.toLowerCase().replace(/\s+/g, '_'));

  const alerts = input.metric_data.slice(1).map((row, idx) => {
    const alertObj = {
      alert_type: input.rows[idx + 1]
    };

    row.forEach((value, colIndex) => {
      const key = colMap[colIndex];
      alertObj[key] = String(value);
    });

    return alertObj;
  });

  const totalRow = input.metric_data[0];

  const totals = {};
  colMap.forEach((key, i) => {
    totals[`total_${key}`] = String(totalRow[i]);
  });

  totals.total_open = String(
    (Number(totals.total_new) || 0) + (Number(totals.total_in_progress) || 0) + (Number(totals.total_overdue) || 0)
  );

  return {
    ...totals,
    alerts,
    title: 'Alert Summary',
    subTitle: timeperiodLabel
  };
}

export function formatNumberToDecimalPlaces(value, decimals = 2, defaultValue = "--") {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(decimals) : defaultValue;
}


export const normalizeBackendData = ({
  columns = [],
  rows = [],
  metric_data = [],
  rowLabelKey = 'label',
}) => {
  if (metric_data?.length && !rows?.length) {
    return {
      categories: columns.map(c => c.replace('num_', '').replaceAll('_', ' ')),
      series: [
        {
          name: 'Count',
          data: metric_data[0],
        },
      ],
    };
  }

  if (rows?.length) {
    return {
      categories: rows.map(r => r[rowLabelKey]),
      series: columns.map((col) => ({
        name: col.replace('num_', '').replaceAll('_', ' '),
        data: rows.map(r => r[col] ?? 0),
      })),
    };
  }

  return { categories: [], series: [] };
};


export const toPieSeries = (canonical) => ({
  series: [
    {
      type: 'pie',
      data: canonical.categories.map((name, index) => ({
        name,
        y: canonical.series[0]?.data[index] ?? 0,
      })),
    },
  ],
});
export function transformAlertMetricsByProperty(input = {}) {
  if (!input?.metric_data?.length || !input?.columns?.length || !input?.rows?.length) return {};

  const normalizeKey = col => col.toLowerCase().replace(/\s+/g, '_');

  const colMap = input.columns.map(normalizeKey);

  const alerts = input.metric_data.map((row, idx) => {
    const obj = { property: input.rows[idx] };

    row.forEach((value, colIndex) => {
      const key = colMap[colIndex];
      obj[key] = Number(value);
    });

    return obj;
  });

  return { alerts };
}
